/**
 * PWA Install Prompt Component
 *
 * Shows a dismissible banner prompting users to install the app.
 * Uses the beforeinstallprompt event to provide a native install experience.
 */

import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallPromptProps {
  onInstalled?: () => void;
}

export const InstallPrompt: React.FC<InstallPromptProps> = ({ onInstalled }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as Navigator & { standalone?: boolean }).standalone
      || document.referrer.includes('android-app://');

    if (isStandalone) {
      return; // Already installed, don't show prompt
    }

    // Check if user dismissed recently (don't show for 7 days)
    const dismissedAt = localStorage.getItem('ring_ash_install_dismissed');
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      if (dismissedDate > sevenDaysAgo) {
        return; // Dismissed recently, don't show
      }
    }

    // Detect iOS for special handling
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & { MSStream?: unknown }).MSStream;
    setIsIOS(isIOSDevice);

    // For iOS, show banner after a delay (no beforeinstallprompt event)
    if (isIOSDevice) {
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Listen for the beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 2000);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setShowBanner(false);
      setDeferredPrompt(null);
      onInstalled?.();
      console.log('PWA was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [onInstalled]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // iOS or no prompt available - just show instructions
      return;
    }

    setIsInstalling(true);

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted install prompt');
        onInstalled?.();
      } else {
        console.log('User dismissed install prompt');
      }
    } catch (error) {
      console.error('Install prompt error:', error);
    } finally {
      setIsInstalling(false);
      setDeferredPrompt(null);
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('ring_ash_install_dismissed', new Date().toISOString());
  };

  if (!showBanner) return null;

  // iOS-specific instructions
  if (isIOS) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
        <div className="bg-gradient-to-r from-amber-600 to-amber-500 rounded-2xl p-4 shadow-xl border border-amber-400/30">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1.5 text-white/70 hover:text-white"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-start gap-3 pr-6">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white text-sm">Install Ring & Ash</h3>
              <p className="text-white/80 text-xs mt-1 leading-relaxed">
                Tap the share button <span className="inline-block px-1 bg-white/20 rounded text-[10px]">⬆</span> then "Add to Home Screen"
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Standard install prompt
  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
      <div className="bg-gradient-to-r from-amber-600 to-amber-500 rounded-2xl p-4 shadow-xl border border-amber-400/30">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1.5 text-white/70 hover:text-white"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 pr-6">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-white text-sm">Install Ring & Ash</h3>
            <p className="text-white/80 text-xs">Quick access from your home screen</p>
          </div>
          <button
            onClick={handleInstall}
            disabled={isInstalling}
            className="px-4 py-2 bg-white text-amber-600 rounded-lg text-sm font-semibold hover:bg-amber-50 transition-colors disabled:opacity-70"
          >
            {isInstalling ? 'Installing...' : 'Install'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Floating Install Button (alternative minimal UI)
 * Shows a small floating button that triggers install
 */
export const InstallButton: React.FC<InstallPromptProps> = ({ onInstalled }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as Navigator & { standalone?: boolean }).standalone;

    if (isStandalone) return;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      onInstalled?.();
    }

    setDeferredPrompt(null);
    setCanInstall(false);
  };

  if (!canInstall) return null;

  return (
    <button
      onClick={handleInstall}
      className="fixed bottom-24 right-4 z-50 w-12 h-12 bg-amber-500 rounded-full shadow-lg flex items-center justify-center hover:bg-amber-400 transition-colors"
      aria-label="Install app"
    >
      <Download className="w-5 h-5 text-white" />
    </button>
  );
};

export default InstallPrompt;
